/**
 * Pure focus layout primitives shared by the planners: one frame as a
 * managed window, frames in given boxes, and the reads of the boxes a
 * presentation already holds. Several frames without given boxes are laid
 * out as rounds (`focus-plan-rounds.ts`). Planners take `(state, input,
 * env)` and return the next state; adapters draw it.
 */
import {
  calculateFocusWindowBox,
  calculateNormalFocusWindowBox,
  type FocusWindowBox,
  type FocusWindowMode,
} from "@/lib/focus-window-layout";
import { calculateProportionalNormalBox } from "@/lib/focus/focus-container-boxes";
import type {
  FocusEnterKind,
  FocusMotion,
  FocusNaturalSize,
  PresentationState,
} from "@/lib/focus/focus-types";

export interface FocusPlanEnv {
  /** This window's focus surface. */
  surface: FocusWindowBox;
  naturalSize: (frameId: string) => FocusNaturalSize;
  /** The size in an agent round; the natural size when absent. */
  roundSize?: (frameId: string) => FocusNaturalSize;
  /** Undefined when the frame does not exist. */
  frameType: (frameId: string) => string | undefined;
  /** Where a frame sits outside focus, when its renderer places it. */
  workspaceBox: (frameId: string) => FocusWindowBox | null;
}

export function focusBoxesMatch(a: FocusWindowBox, b: FocusWindowBox): boolean {
  return (
    Math.abs(a.x - b.x) <= 1 &&
    Math.abs(a.y - b.y) <= 1 &&
    Math.abs(a.width - b.width) <= 1 &&
    Math.abs(a.height - b.height) <= 1
  );
}

export function isConversationFrame(
  env: FocusPlanEnv,
  frameId: string | undefined,
): boolean {
  return !!frameId && env.frameType(frameId) === "conversation";
}

export function focusMotion(
  state: PresentationState | null,
  frameId: string,
  kind: FocusEnterKind,
): FocusMotion {
  return { frameId, kind, seq: (state?.motion?.seq ?? 0) + 1 };
}

export function naturalSizeIn(
  state: PresentationState,
  frameId: string,
  env: FocusPlanEnv,
): FocusNaturalSize {
  return state.naturalSizes[frameId] ?? env.naturalSize(frameId);
}

/** Sizes of the presented frames: kept ones stay, new ones are captured. */
function presentedNaturalSizes(
  state: PresentationState,
  env: FocusPlanEnv,
): Record<string, FocusNaturalSize> {
  return Object.fromEntries(
    state.frameIds.map((id) => [id, naturalSizeIn(state, id, env)]),
  );
}

function frameBoxIn(
  state: PresentationState,
  frameId: string,
): FocusWindowBox | null {
  const index = state.frameIds.indexOf(frameId);
  return (index >= 0 && state.boxes[index]) || null;
}

/** One frame as a managed window: normal, maximized or snapped. */
export function planFocusWindow(
  state: PresentationState,
  frameId: string,
  mode: FocusWindowMode,
  options: { normalBox?: FocusWindowBox },
  env: FocusPlanEnv,
): PresentationState | null {
  const index = state.frameIds.indexOf(frameId);
  if (state.variant !== "focus" || index < 0) return null;
  const { surface } = env;
  const existingWindow =
    state.focusWindow?.frameId === frameId ? state.focusWindow : null;
  // Single-frame focus keeps the workspace aspect ratio: the natural size is
  // fitted inside the comfortable normal box instead of stretched to it.
  const proportionalNormalBox =
    mode === "normal" && !options.normalBox && !existingWindow?.normalBox
      ? calculateProportionalNormalBox(
          surface,
          naturalSizeIn(state, frameId, env),
        )
      : null;
  const normalBox =
    options.normalBox ??
    existingWindow?.normalBox ??
    (mode !== "normal" ? frameBoxIn(state, frameId) : null) ??
    proportionalNormalBox ??
    calculateNormalFocusWindowBox(surface);
  const box =
    proportionalNormalBox ?? calculateFocusWindowBox(mode, surface, normalBox);
  return {
    ...state,
    activeIndex: index,
    boxes: state.frameIds.map((_, i) =>
      i === index ? box : (state.boxes[i] ?? box),
    ),
    naturalSizes: presentedNaturalSizes(state, env),
    focusWindow: {
      frameId,
      mode,
      normalBox: proportionalNormalBox ?? normalBox,
    },
    surface,
    // Rounds keep their main slot: the window goes back to its place.
    customFocusLayout:
      state.customFocusLayout?.kind === "rounds"
        ? state.customFocusLayout
        : undefined,
  };
}

/** The presented frames in `boxes`; one frame may be a managed window. */
export function planFocusBoxes(
  state: PresentationState,
  activeId: string,
  options: {
    boxes: FocusWindowBox[];
    focusWindowMode?: FocusWindowMode | null;
    preserveCustomFocusLayout?: boolean;
  },
  env: FocusPlanEnv,
): PresentationState | null {
  if (state.variant !== "focus") return null;
  const frameIds = state.frameIds;
  const boxes = options.boxes.slice(0, frameIds.length);
  const focusWindow =
    frameIds.length === 1 && options.focusWindowMode && boxes[0]
      ? {
          frameId: activeId,
          mode: options.focusWindowMode,
          normalBox: boxes[0],
        }
      : undefined;
  return {
    ...state,
    boxes,
    naturalSizes: presentedNaturalSizes(state, env),
    focusWindow,
    surface: env.surface,
    customFocusLayout: options.preserveCustomFocusLayout
      ? state.customFocusLayout && { ...state.customFocusLayout, boxes }
      : undefined,
  };
}
